import { useEffect, useState, useCallback } from "react";
import { fetchSuppliers, createSupplier, updateSupplier, deleteSupplier, enrichSupplier } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { errorMessage } from "@/lib/errors";
import { Plus, Pencil, Trash2, ExternalLink, Search, Sparkles, Loader2, Phone, Mail, Store as StoreIcon } from "lucide-react";

const EMPTY = { name: "", website: "", country: "", specialties_csv: "", notes: "", phone: "", email: "", logo_path: "", is_active: true };

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qLive, setQLive] = useState("");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchSuppliers({ q: q || undefined, active_only: false });
      setItems(r.items);
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => setQ(qLive), 250);
    return () => clearTimeout(t);
  }, [qLive]);

  const onSaved = () => { setSheetOpen(false); setEditing(null); load(); };
  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      await deleteSupplier(confirmDel.id);
      toast.success(`Removed ${confirmDel.name}`);
      setConfirmDel(null);
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="px-6 md:px-10 py-8 pb-28 max-w-[1600px]">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#3A2A10] mb-2">Sourcing</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#0A0604] leading-none">Suppliers</h1>
          <div className="text-sm text-[#3A2A10] mt-3 font-mono-data">
            {loading ? "…" : `${items.length} vendors`}
          </div>
        </div>
        <Button
          onClick={() => { setEditing(null); setSheetOpen(true); }}
          className="h-9 px-4 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10] text-xs uppercase tracking-wider rounded-sm"
          data-testid="add-supplier-button"
        >
          <Plus size={14} className="mr-1.5" /> Add Supplier
        </Button>
      </header>

      <div className="relative mb-6 max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#3A2A10]" />
        <Input
          value={qLive}
          onChange={(e) => setQLive(e.target.value)}
          placeholder="Search name, country, notes…"
          className="pl-9 h-10 bg-[#F9F0D4] border-[#8B6A1F]/45 rounded-sm focus-visible:ring-[#D4AF37]/40"
          data-testid="suppliers-search"
        />
      </div>

      {!loading && items.length === 0 ? (
        <div className="border border-[#8B6A1F]/35 bg-[#F9F0D4] py-20 text-center" data-testid="suppliers-empty">
          <div className="font-serif-display text-2xl text-[#0A0604] mb-2">No suppliers yet</div>
          <div className="text-xs text-[#3A2A10] uppercase tracking-[0.2em]">Add your first vendor to begin sourcing</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="suppliers-grid">
          {items.map((s) => (
            <article
              key={s.id}
              className={`group bg-[#F9F0D4] border border-[#8B6A1F]/35 p-5 hover:border-[#D4AF37]/30 transition-colors flex flex-col gap-3 ${s.is_active === false ? "opacity-50" : ""}`}
              data-testid={`supplier-card-${s.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <SupplierLogo supplier={s} />
                  <div className="min-w-0">
                    <div className="font-serif-display text-xl text-[#0A0604] leading-tight truncate">{s.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10] mt-1">
                      {s.country || "—"}{s.is_active === false ? " · Inactive" : ""}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => { setEditing(s); setSheetOpen(true); }} className="p-1.5 text-[#3A2A10] hover:text-[#3A2A10]" data-testid={`supplier-edit-${s.id}`} aria-label="Edit"><Pencil size={13} /></button>
                  <button onClick={() => setConfirmDel(s)} className="p-1.5 text-[#3A2A10] hover:text-rose-400" data-testid={`supplier-delete-${s.id}`} aria-label="Delete"><Trash2 size={13} /></button>
                </div>
              </div>

              {s.website && (
                <a
                  href={s.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-[#3A2A10] hover:text-[#E5C158] truncate"
                  data-testid={`supplier-website-${s.id}`}
                >
                  {s.website.replace(/^https?:\/\//, "")} <ExternalLink size={11} className="shrink-0" />
                </a>
              )}

              {(s.phone || s.email) && (
                <div className="flex flex-col gap-1 text-xs text-[#3A2A10]">
                  {s.phone && (
                    <a href={`tel:${s.phone.replace(/[^+\d]/g, "")}`} className="inline-flex items-center gap-1.5 hover:text-[#0A0604]" data-testid={`supplier-phone-${s.id}`}>
                      <Phone size={11} className="shrink-0" /> <span className="font-mono-data truncate">{s.phone}</span>
                    </a>
                  )}
                  {s.email && (
                    <a href={`mailto:${s.email}`} className="inline-flex items-center gap-1.5 hover:text-[#0A0604]" data-testid={`supplier-email-${s.id}`}>
                      <Mail size={11} className="shrink-0" /> <span className="truncate">{s.email}</span>
                    </a>
                  )}
                </div>
              )}

              {Array.isArray(s.specialties) && s.specialties.length > 0 && (
                <div className="flex items-center flex-wrap gap-1">
                  {s.specialties.map((sp) => (
                    <span key={sp} className="text-[10px] uppercase tracking-wider text-[#3A2A10] border border-[#8B6A1F]/45 px-1.5 py-0.5 rounded-sm">
                      {sp}
                    </span>
                  ))}
                </div>
              )}

              {s.notes && (
                <p className="text-xs text-[#3A2A10] leading-relaxed border-t border-[#8B6A1F]/30 pt-2">{s.notes}</p>
              )}
            </article>
          ))}
        </div>
      )}

      <SupplierFormSheet
        open={sheetOpen}
        onOpenChange={(o) => { setSheetOpen(o); if (!o) setEditing(null); }}
        supplier={editing}
        onSaved={onSaved}
      />

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="bg-[#F9F0D4] border-[#8B6A1F]/45 rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-2xl">Remove supplier?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#3A2A10]">
              Delete <span className="text-[#0A0604]">{confirmDel?.name}</span>? Parts already linked by name will keep their supplier text.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#8B6A1F]/45 rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700 text-white rounded-sm" data-testid="supplier-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SupplierFormSheet({ open, onOpenChange, supplier, onSaved }) {
  const [data, setData] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [enriching, setEnriching] = useState(false);

  useEffect(() => {
    if (supplier) {
      setData({
        name: supplier.name || "",
        website: supplier.website || "",
        country: supplier.country || "",
        specialties_csv: Array.isArray(supplier.specialties) ? supplier.specialties.join(", ") : "",
        notes: supplier.notes || "",
        phone: supplier.phone || "",
        email: supplier.email || "",
        logo_path: supplier.logo_path || "",
        is_active: supplier.is_active !== false,
      });
    } else {
      setData(EMPTY);
    }
  }, [supplier, open]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const handleEnrich = async () => {
    const url = (data.website || "").trim();
    // Button is already disabled={!data.website.trim()} so no guard needed here.
    setEnriching(true);
    try {
      const result = await enrichSupplier(url);
      setData((d) => ({
        ...d,
        // only fill fields the user hasn't already typed — never overwrite
        name: d.name || result.name || "",
        country: d.country || result.country || "",
        specialties_csv: d.specialties_csv || (result.specialties || []).join(", "),
        notes: d.notes || result.notes || "",
        phone: d.phone || result.phone || "",
        email: d.email || result.email || "",
        // logo is always refreshed from the enrich response (no user input for it)
        logo_path: result.logo_path || d.logo_path || "",
      }));
      toast.success("Populated from website");
    } catch (err) {
      const status = err?.response?.status;
      let msg;
      if (status === 502) msg = "Could not read that website — check the URL.";
      else if (status === 422) msg = "That page didn't have enough text to analyze.";
      else msg = errorMessage(err, "Could not enrich from website");
      toast.error(msg);
    } finally {
      setEnriching(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!data.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: data.name.trim(),
        website: data.website.trim() || null,
        country: data.country.trim() || null,
        specialties: data.specialties_csv
          .split(",").map((x) => x.trim()).filter(Boolean),
        notes: data.notes.trim() || null,
        phone: data.phone.trim() || null,
        email: data.email.trim() || null,
        logo_path: data.logo_path || null,
        is_active: !!data.is_active,
      };
      if (supplier?.id) {
        await updateSupplier(supplier.id, payload);
        toast.success("Supplier updated");
      } else {
        await createSupplier(payload);
        toast.success("Supplier added");
      }
      onSaved?.();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg bg-[#FBF3D9] border-l border-[#8B6A1F]/45 text-[#0A0604] overflow-y-auto pb-10" data-testid="supplier-form-sheet">
        <SheetHeader className="mb-6">
          <SheetTitle className="font-serif-display text-3xl">{supplier ? "Edit Supplier" : "New Supplier"}</SheetTitle>
          <SheetDescription className="text-[#3A2A10] text-xs uppercase tracking-[0.25em]">Sourcing contact</SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="supplier-form">
          <Field label="Name *">
            <Input value={data.name} onChange={(e) => set("name", e.target.value)} className="dark-input" required data-testid="supplier-form-name" />
          </Field>
          <Field label="Website">
            <div className="flex gap-2">
              <Input value={data.website} onChange={(e) => set("website", e.target.value)} placeholder="https://…" className="dark-input flex-1" data-testid="supplier-form-website" />
              <Button
                type="button"
                onClick={handleEnrich}
                disabled={enriching || !data.website.trim()}
                variant="outline"
                className="bg-transparent border-[#D4AF37]/40 text-[#3A2A10] hover:bg-[#D4AF37]/[0.08] hover:text-[#E5C158] rounded-sm shrink-0"
                data-testid="supplier-enrich-btn"
                title="Auto-fill the rest from the website"
              >
                {enriching ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Sparkles size={13} className="mr-1.5" />}
                {enriching ? "Reading…" : "Enrich"}
              </Button>
            </div>
            <div className="text-[10px] text-[#3A2A10] mt-1">
              Click Enrich after entering the URL — the app reads the site and suggests name, country, phone, email, logo, specialties and notes.
            </div>
          </Field>
          <Field label="Country">
            <Input value={data.country} onChange={(e) => set("country", e.target.value)} className="dark-input" data-testid="supplier-form-country" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone">
              <Input value={data.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+1 555-…" className="dark-input font-mono-data" data-testid="supplier-form-phone" />
            </Field>
            <Field label="Email">
              <Input type="email" value={data.email} onChange={(e) => set("email", e.target.value)} placeholder="sales@…" className="dark-input" data-testid="supplier-form-email" />
            </Field>
          </div>
          {data.logo_path && (
            <div className="flex items-center gap-3 border border-[#8B6A1F]/35 rounded-sm p-3 bg-[#F9F0D4]/60">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${data.logo_path}`}
                alt="Logo"
                className="h-10 w-10 object-contain"
                data-testid="supplier-form-logo-preview"
              />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#3A2A10]">Logo fetched</div>
                <div className="text-xs text-[#3A2A10] truncate">{data.logo_path}</div>
              </div>
              <button type="button" onClick={() => set("logo_path", "")} className="text-[#3A2A10] hover:text-rose-400 text-xs" data-testid="supplier-form-logo-clear">Remove</button>
            </div>
          )}
          <Field label="Specialties (comma-separated)">
            <Input value={data.specialties_csv} onChange={(e) => set("specialties_csv", e.target.value)} placeholder="Movements, Tools, Batteries…" className="dark-input" data-testid="supplier-form-specs" />
          </Field>
          <div className="text-[10px] text-[#3A2A10] -mt-3 leading-snug">
            Note: parts keep a supplier's <span className="text-[#3A2A10]">name</span> as a label — renaming a vendor here will not rename it on existing parts.
          </div>
          <Field label="Notes">
            <Textarea value={data.notes} onChange={(e) => set("notes", e.target.value)} className="dark-input min-h-20" data-testid="supplier-form-notes" />
          </Field>
          <label className="flex items-center gap-2 text-xs text-[#3A2A10] cursor-pointer">
            <input
              type="checkbox"
              checked={data.is_active}
              onChange={(e) => set("is_active", e.target.checked)}
              className="accent-[#D4AF37]"
              data-testid="supplier-form-active"
            />
            Active
          </label>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-[#8B6A1F]/35">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-[#3A2A10] hover:text-[#0A0604]" data-testid="supplier-form-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10] uppercase tracking-wider text-xs rounded-sm" data-testid="supplier-form-submit">
              {saving ? "Saving…" : supplier ? "Save Changes" : "Add Supplier"}
            </Button>
          </div>
        </form>

        <style>{`.dark-input{background-color: #F9F0D4 !important;border-color: rgba(139,106,31,0.25) !important;border-radius:2px!important;color: #2B2214 !important}.dark-input:focus,.dark-input:focus-visible{border-color:rgba(212,175,55,0.5)!important;outline:none!important;box-shadow:0 0 0 1px rgba(212,175,55,0.3)!important}`}</style>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.2em] text-[#3A2A10] font-normal">{label}</Label>
      {children}
    </div>
  );
}

function SupplierLogo({ supplier }) {
  const [failed, setFailed] = useState(false);
  if (!supplier.logo_path || failed) {
    return (
      <div className="h-10 w-10 shrink-0 flex items-center justify-center bg-[#2B2214]/[0.06] border border-[#8B6A1F]/35 rounded-sm text-[#3A2A10]" data-testid={`supplier-logo-fallback-${supplier.id}`}>
        <StoreIcon size={16} />
      </div>
    );
  }
  return (
    <img
      src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${supplier.logo_path}`}
      alt=""
      onError={() => setFailed(true)}
      className="h-10 w-10 shrink-0 object-contain bg-[#FBF3D9] border border-[#8B6A1F]/35 rounded-sm p-1"
      data-testid={`supplier-logo-${supplier.id}`}
    />
  );
}
